import { useCallback, useEffect, useRef, useState } from "react";

interface UseResizableWidthOptions {
  /** Persists the width across visits — key it uniquely per panel. */
  storageKey: string;
  defaultWidth: number;
  min: number;
  max: number;
  /** true when the drag handle sits on the panel's LEFT edge (dragging left
   * grows it) — e.g. a panel anchored to the right side of the layout. */
  invert?: boolean;
}

/** Drag-to-resize width for a side panel, persisted to localStorage. Returns
 * the current width and a pointerdown handler to put on that panel's resize
 * handle. */
export function useResizableWidth({ storageKey, defaultWidth, min, max, invert = false }: UseResizableWidthOptions) {
  const [width, setWidth] = useState<number>(() => {
    try {
      const stored = Number(localStorage.getItem(storageKey));
      return Number.isFinite(stored) && stored > 0 ? Math.min(max, Math.max(min, stored)) : defaultWidth;
    } catch {
      return defaultWidth;
    }
  });

  const startXRef = useRef(0);
  const startWidthRef = useRef(width);
  const draggingRef = useRef(false);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!draggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      const signedDelta = invert ? -delta : delta;
      setWidth(Math.min(max, Math.max(min, startWidthRef.current + signedDelta)));
    },
    [invert, max, min],
  );

  const stopDragging = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", stopDragging);
  }, [onPointerMove]);

  const startDragging = useCallback(
    (e: React.PointerEvent) => {
      draggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = width;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", stopDragging);
    },
    [onPointerMove, stopDragging, width],
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(width));
    } catch {
      // Private-browsing/storage-disabled — resizing still works for this
      // session, it just won't be remembered next time.
    }
  }, [storageKey, width]);

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stopDragging);
    };
  }, [onPointerMove, stopDragging]);

  return { width, startDragging };
}
